import { useEffect, useMemo, useState } from 'react';
import { CheckSquare, Clock, AlertCircle, CheckCircle2, MessageSquare } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { tasksApi, ApiError } from '@solara/api-client';
import type { InboxBucketDto, TaskDto } from '@solara/api-client';

const PRIORITY_TONES: Record<string, 'danger' | 'warning' | 'info' | 'neutral'> = {
  URGENT: 'danger',
  CRITICAL: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'neutral',
};

export default function MyTasksPage() {
  const { t } = useTranslation('p1');
  const bucketLabel = (c: InboxBucketDto['category']) =>
    t(`tasks.buckets.${c}`, { defaultValue: c });
  const [tasks, setTasks] = useState<TaskDto[]>([]);
  const [buckets, setBuckets] = useState<InboxBucketDto[]>([]);
  const [filter, setFilter] = useState<'all' | InboxBucketDto['category']>('all');
  const [comment, setComment] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [t, b] = await Promise.all([tasksApi.list('mine'), tasksApi.buckets()]);
      setTasks(t);
      setBuckets(b);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  const filteredTasks = useMemo(() => {
    if (filter === 'all') return tasks;
    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date(startOfToday);
    endOfToday.setDate(endOfToday.getDate() + 1);
    const endOfWeek = new Date(startOfToday);
    endOfWeek.setDate(endOfWeek.getDate() + 7);
    return tasks.filter((t) => {
      const due = t.dueDate ? new Date(t.dueDate) : null;
      if (filter === 'NO_DUE') return !due;
      if (!due) return false;
      if (filter === 'OVERDUE') return due < startOfToday;
      if (filter === 'TODAY') return due >= startOfToday && due < endOfToday;
      if (filter === 'THIS_WEEK') return due >= endOfToday && due < endOfWeek;
      if (filter === 'LATER') return due >= endOfWeek;
      return false;
    });
  }, [tasks, filter]);

  async function complete(taskId: string) {
    setBusy(true);
    setError(null);
    try {
      await tasksApi.transition(taskId, 'COMPLETED');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function postComment(taskId: string) {
    const text = comment[taskId];
    if (!text) return;
    setBusy(true);
    try {
      await tasksApi.comment(taskId, text);
      setComment((c) => ({ ...c, [taskId]: '' }));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('tasks.title')}</h1>
        <p className="text-fg-muted text-sm">{t('tasks.subtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-5">
        {(['OVERDUE', 'TODAY', 'THIS_WEEK', 'LATER', 'NO_DUE'] as const).map((cat) => (
          <BucketChip
            key={cat}
            current={filter}
            bucket={{ category: cat, count: count(buckets, cat) }}
            label={bucketLabel(cat)}
            onClick={() => setFilter(cat)}
          />
        ))}
      </div>
      {filter !== 'all' && (
        <button onClick={() => setFilter('all')} className="text-xs text-brand-700 hover:underline">
          {t('tasks.showAll')}
        </button>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            {t('tasks.tasksCount', { count: filteredTasks.length })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {filteredTasks.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('tasks.emptyInbox')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {filteredTasks.map((task) => (
                <li key={task.id} className="py-3 text-sm space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{task.title}</p>
                      {task.description && <p className="text-xs text-fg-muted">{task.description}</p>}
                      <p className="text-[11px] text-fg-muted">
                        {task.taskType}
                        {task.relatedEntityType ? ` · ${task.relatedEntityType}` : ''}
                        {task.dueDate && (
                          <span>
                            {' '}
                            · {t('tasks.due')} {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone={PRIORITY_TONES[task.priority] ?? 'neutral'} className="text-[10px]">
                        {t(`tasks.priorityLabels.${task.priority}`, { defaultValue: task.priority })}
                      </Badge>
                      <Button variant="ghost" onClick={() => void complete(task.id)} disabled={busy}>
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('tasks.complete')}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-3 w-3 text-fg-muted" />
                    <Input
                      value={comment[task.id] ?? ''}
                      onChange={(e) => setComment((c) => ({ ...c, [task.id]: e.target.value }))}
                      placeholder={t('tasks.addComment')}
                      className="text-xs"
                    />
                    <Button
                      variant="ghost"
                      onClick={() => void postComment(task.id)}
                      disabled={busy || !comment[task.id]}
                    >
                      {t('tasks.post')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function count(buckets: InboxBucketDto[], category: InboxBucketDto['category']): number {
  return buckets.find((b) => b.category === category)?.count ?? 0;
}

function BucketChip({
  current,
  bucket,
  label,
  onClick,
}: {
  current: 'all' | InboxBucketDto['category'];
  bucket: InboxBucketDto;
  label: string;
  onClick: () => void;
}) {
  const active = current === bucket.category;
  return (
    <button
      onClick={onClick}
      className={`rounded-xl border px-3 py-2 text-sm text-start transition-colors ${
        active ? 'border-brand-500 bg-brand-50' : 'border-border bg-surface hover:bg-surface-muted'
      }`}
    >
      <div className="flex items-center gap-2 text-xs text-fg-muted">
        <Clock className="h-3 w-3" />
        {label}
      </div>
      <div className="text-lg font-semibold">{bucket.count}</div>
    </button>
  );
}
